import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Trophy, Flame } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

export default function Leaderboard() {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [pref, setPref] = useState({ opt_in: false, display_name: "" });
  const [name, setName] = useState("");
  const loadRows = () => api.get("/leaderboard").then((r) => setRows(r.data));
  useEffect(() => {
    loadRows();
    api.get("/leaderboard/me").then((r) => { setPref(r.data); setName(r.data.display_name || ""); }).catch(() => {});
  }, []);
  const savePref = async (opt_in) => {
    try {
      const r = await api.put("/leaderboard/me", { opt_in, display_name: name });
      setPref({ opt_in: r.data.opt_in, display_name: r.data.display_name });
      toast.success(opt_in ? t("leaderboard.saved") : t("leaderboard.hidden"));
      loadRows();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const max = Math.max(1, ...rows.map((r) => r.xp || 0));

  return (
    <div className="space-y-8 max-w-4xl" data-testid="leaderboard-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("leaderboard.leaderboard")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("leaderboard.topStudents")}</h1>
        <p className="mt-2 text-foreground/70">{t("leaderboard.friendlyCompetitionKeepYourStreak")}</p>
      </header>

      <section className="hifz-card" data-testid="leaderboard-optin">
        <h2 className="font-display font-semibold text-lg text-forest-700">{t("leaderboard.showMeTitle")}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t("leaderboard.showMeBody")}</p>
        <div className="mt-3 flex gap-2 flex-wrap items-center">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30}
            placeholder={t("leaderboard.displayName")} data-testid="leaderboard-name"
            className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" />
          <button className="btn-primary text-xs" onClick={() => savePref(true)} disabled={name.trim().length < 2} data-testid="leaderboard-save">
            {t("leaderboard.save")}
          </button>
          {pref.opt_in && (
            <button className="btn-ghost text-xs" onClick={() => savePref(false)} data-testid="leaderboard-hide">{t("leaderboard.hideMe")}</button>
          )}
        </div>
        {pref.opt_in && <p className="text-xs text-forest-700 mt-2">{t("leaderboard.youAreListed", { name: pref.display_name })}</p>}
      </section>

      <div className="hifz-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sand-200/80 text-muted-foreground uppercase text-xs tracking-[0.18em]">
            <tr>
              <th className="py-3 px-5 text-left">#</th>
              <th className="py-3 px-5 text-left">{t("leaderboard.student")}</th>
              <th className="py-3 px-5 text-left">{t("profile.streak")}</th>
              <th className="py-3 px-5 text-left">{t("profile.ayahs")}</th>
              <th className="py-3 px-5 text-left">{t("profile.xp")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.user_id}-${i}`} className="border-t border-sand-300" data-testid={`leaderboard-row-${i}`}>
                <td className="py-3 px-5 font-display font-semibold">
                  {i === 0 ? <Trophy className="h-4 w-4 text-gold-600 inline" /> : <span className="text-muted-foreground">{i + 1}</span>}
                </td>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-3">
                    {r.picture ? (
                      <img src={r.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-forest-700 text-sand-100 grid place-items-center text-xs font-semibold">
                        {(r.name || "U")[0]}
                      </div>
                    )}
                    <span className="font-medium">{r.name}</span>
                  </div>
                </td>
                <td className="py-3 px-5"><Flame className="inline h-3.5 w-3.5 text-terracotta-500 mr-1" />{r.streak || 0}</td>
                <td className="py-3 px-5">{r.total_memorized || 0}</td>
                <td className="py-3 px-5 w-1/3">
                  <div className="flex items-center gap-3">
                    <div className="font-display font-semibold text-forest-700">{r.xp || 0}</div>
                    <div className="flex-1 h-1.5 bg-sand-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-forest-600 to-gold-500"
                        style={{ width: `${((r.xp || 0) / max) * 100}%` }} />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td className="py-10 text-center text-muted-foreground" colSpan={5}>{t("leaderboard.noStudentsYet")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
